import { uniqBy } from 'lodash-es';
import type { ConversationFilterAdvance } from './conversationsHooks/useFilterAdvance';
import type { TimeSortType, TodoFilterType } from './conversationsHooks/useCategoryFilterState';
import type { ConversationCategoryType } from './conversationsHooks/useConversationCategory';
import { ApiQueryKey } from 'services/config/queryKey';

type ConversationListStoreFilter = {
  region?: General.UpperCaseRegion;
  platform?: General.PlatformUpperCaseTunnel;
  follower?: '0' | '1';
  storeList?: Array<{ tunnelAccountId: string }>;
};

export interface BuildConversationListQueryKeyParams {
  category: ConversationCategoryType;
  todoFilter?: TodoFilterType;
  sort?: TimeSortType;
  filterAdvance?: ConversationFilterAdvance;
  storeFilter: ConversationListStoreFilter;
  isConversationDescMode: boolean;
}

export interface ConversationPageResult {
  items: Conversation.Conversation[];
  nextOffset: string;
  hasMore: boolean;
}

export const buildConversationListQueryKey = ({
  category,
  todoFilter,
  sort,
  filterAdvance,
  storeFilter,
  isConversationDescMode,
}: BuildConversationListQueryKeyParams) => {
  const tunnelAccountIds = storeFilter.storeList?.map((item) => item.tunnelAccountId) ?? [];

  return [
    ApiQueryKey.ConversationList,
    {
      category,
      todoFilter,
      sort,
      filterAdvance,
      region: storeFilter.region,
      platform: storeFilter.platform,
      follower: storeFilter.follower,
      tunnelAccountIds,
      isConversationDescMode,
    },
  ] as const;
};

export const mergeConversationPageItems = (
  primary: Conversation.Conversation[],
  secondary: Conversation.Conversation[] = [],
) => {
  const merged = [...secondary, ...primary];
  const lastItemById = new Map(merged.map((item) => [item.conversationId, item]));

  return uniqBy(merged, 'conversationId').map((item) => {
    return lastItemById.get(item.conversationId) ?? item;
  });
};

export const normalizeConversationPage = (
  page: Partial<ConversationPageResult>,
): ConversationPageResult => ({
  items: page.items ?? [],
  nextOffset: page.nextOffset ?? '0',
  hasMore: page.hasMore ?? false,
});

export const getNextOffsetFromTalkInfo = (
  talkInfo: Conversation.Conversation[],
  dir: 0 | 1 | 2 | 3,
) => {
  const last = talkInfo[talkInfo.length - 1];
  if (!last) {
    return '0';
  }

  return dir === 2 || dir === 3 ? last.fstUnprocessedMsgTimestamp || '0' : last.lastMsgTime || '0';
};
